import { useMutation } from '@tanstack/react-query';
import {
  patch as patchProfile,
  getById as getProfile,
  setActiveProfile,
  uploadAvatar,
  deleteAvatar,
} from '../../services/profileService';
import { requestPhoneChangeCode, confirmPhoneChange } from '../../services/authService';
import type { Profile } from '../../types/profile';
import type { NotificationSettings } from '../../types/profileSettings';

/**
 * Account (own-profile) mutations. Each one mirrors the fresh profile into
 * the profile store, which is the single owner of the signed-in profile.
 *
 * Mutations whose errors the form shows inline (a 409 duplicate email, a
 * wrong verification code) set `meta.silent` so the global error modal stays
 * out of the way.
 */

async function patchAndRefresh(fields: Partial<Profile> & { id: string }): Promise<Profile> {
  await patchProfile(fields);
  const updated = await getProfile(fields.id);
  setActiveProfile(updated);
  return updated;
}

export interface NameFields {
  firstName: string;
  lastName: string;
  displayName: string;
}

export function useUpdateName(profileId: string) {
  return useMutation<Profile, Error, NameFields>({
    mutationFn: (fields) => patchAndRefresh({ id: profileId, ...fields }),
  });
}

/** Email changes are not OTP-verified (parity with core). A duplicate is a 409. */
export function useUpdateEmail(profileId: string) {
  return useMutation<Profile, Error, { email: string }>({
    mutationFn: ({ email }) => patchAndRefresh({ id: profileId, email }),
    meta: { silent: true },
  });
}

/** Phone change, step 1: SMS a code to the NEW number. */
export function useRequestPhoneChangeCode() {
  return useMutation<void, Error, { phone: string }>({
    mutationFn: ({ phone }) => requestPhoneChangeCode(phone),
    meta: { silent: true },
  });
}

/** Phone change, step 2: confirm the code; the BE commits the number it stored at send time. */
export function useConfirmPhoneChange() {
  return useMutation<Profile, Error, { code: string }>({
    mutationFn: async ({ code }) => {
      const updated = await confirmPhoneChange(code);
      setActiveProfile(updated);
      return updated;
    },
    meta: { silent: true },
  });
}

/**
 * Alert/notification channel preferences (`metadata.settings.notifications.profile`).
 * Ported from core's useUpdateNotificationSettings. The BE sends on a channel
 * only while its pref is true — so "off" here is what actually stops pushes;
 * a page can't revoke its own browser permission.
 */
export function useUpdateNotificationSettings(profileId: string) {
  return useMutation<Profile, Error, NotificationSettings>({
    mutationFn: async (channels) => {
      const current = await getProfile(profileId);
      const settings = {
        ...current.metadata.settings,
        notifications: { ...current.metadata.settings?.notifications, profile: channels },
      };
      return patchAndRefresh({ id: profileId, metadata: { ...current.metadata, settings } });
    },
  });
}

export function useUploadAvatar() {
  return useMutation<void, Error, File>({ mutationFn: (file) => uploadAvatar(file) });
}

export function useDeleteAvatar() {
  return useMutation<void, Error, void>({ mutationFn: () => deleteAvatar() });
}
